import {
  AlertTriangle,
  Activity,
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  Clock,
  Eye,
  FilePlus,
  Minus,
  Play,
  TrendingDown,
  TrendingUp,
  UserPlus,
  XCircle,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { JournalDuCompte } from '../components/JournalDuCompte';

import {
  lireResume,
  modifierIntervention,
  type CompteurStatut,
  type Resume,
  type StatutIntervention,
} from '../api/interventions';

/**
 * L'écran GARDE les données affichées pendant un rechargement : blanchir la page
 * à chaque changement de période faisait perdre le filtre du journal, le focus,
 * et le fil d'un geste en cours.
 */
interface Etat {
  readonly resume?: Resume;
  readonly chargement: boolean;
  readonly erreur?: string;
}

/** Couleurs de statut, alignées sur les pastilles du reste de l'application. */
const COULEURS: Record<StatutIntervention, { trait: string; puce: string; icone: typeof Clock }> = {
  planifiee: { trait: 'bg-blue-500', puce: 'bg-blue-500', icone: CalendarClock },
  'en-cours': { trait: 'bg-amber-500', puce: 'bg-amber-500', icone: Activity },
  terminee: { trait: 'bg-emerald-500', puce: 'bg-emerald-500', icone: CheckCircle2 },
  annulee: { trait: 'bg-red-500', puce: 'bg-red-500', icone: XCircle },
};

/** « 2026-10-25 » → « 25 oct. 2026 » (affichage seul). */
function dateLisible(date: string): string {
  const mois = [
    'janv.',
    'févr.',
    'mars',
    'avr.',
    'mai',
    'juin',
    'juil.',
    'août',
    'sept.',
    'oct.',
    'nov.',
    'déc.',
  ];
  const [annee = '', m = '', j = ''] = date.split('-');
  const index = Number(m) - 1;
  // Une date illisible s'affiche telle quelle : mieux vaut « ? » que « undefined ».
  if (annee === '' || m === '' || j === '') return date;
  return `${j} ${mois[index] ?? m} ${annee}`;
}

/**
 * La tendance est DÉCIDÉE par le serveur : `sens` porte la flèche, `tonalite`
 * porte la couleur (« une annulation qui monte » n'est pas une bonne nouvelle).
 * L'écran ne fait que dessiner.
 */
function Tendance({ compteur }: { readonly compteur: CompteurStatut }) {
  const { sens, tonalite } = compteur.tendance;

  // Pas de période précédente : ne rien affirmer, pas même « stable ».
  if (sens === 'aucune') return null;

  const classes =
    tonalite === 'positive'
      ? 'text-emerald-600'
      : tonalite === 'negative'
        ? 'text-red-600'
        : 'text-slate-400';

  if (sens === 'stable') {
    return (
      <p className={`text-[11px] font-medium mt-1 flex items-center gap-1 ${classes}`}>
        <Minus className="w-3 h-3" aria-hidden="true" /> Stable
      </p>
    );
  }

  const Icone = sens === 'hausse' ? TrendingUp : TrendingDown;
  const delta = compteur.delta ?? 0;
  return (
    <p className={`text-[11px] font-medium mt-1 flex items-center gap-1 ${classes}`}>
      <Icone className="w-3 h-3" aria-hidden="true" />
      {delta > 0 ? '+' : ''}
      {delta} vs période précédente
    </p>
  );
}

export function TableauDeBord() {
  const [etat, setEtat] = useState<Etat>({ chargement: true });
  /** Période demandée ; `undefined` = défaut du serveur (« ce mois-ci »). */
  const [periode, setPeriode] = useState<string>();
  const [enCours, setEnCours] = useState<string>();
  const [erreurAction, setErreurAction] = useState<string>();
  const [confirmation, setConfirmation] = useState<string>();
  /** Un seul chargement en vol : le précédent est abandonné, jamais concurrent. */
  const enVol = useRef<AbortController | undefined>(undefined);

  const charger = useCallback(async (periodeDemandee?: string) => {
    enVol.current?.abort();
    const controleur = new AbortController();
    enVol.current = controleur;

    // Les données déjà affichées restent : seule la requête change.
    setEtat((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));

    const resultat = await lireResume({
      signal: controleur.signal,
      ...(periodeDemandee ? { periode: periodeDemandee } : {}),
    });
    if (controleur.signal.aborted) return;

    if (resultat.ok && resultat.donnees) {
      setEtat({ resume: resultat.donnees, chargement: false });
    } else {
      // La panne se dit, elle n'efface pas ce qui était lisible.
      setEtat((precedent) => ({
        ...precedent,
        chargement: false,
        erreur: resultat.message ?? 'Erreur inattendue.',
      }));
    }
  }, []);

  useEffect(() => {
    void charger(periode);
    return () => enVol.current?.abort();
  }, [charger, periode]);

  // Une confirmation décrit un geste dans un contexte : elle ne survit pas au
  // changement de période.
  useEffect(() => {
    setConfirmation(undefined);
  }, [periode]);

  /** « Démarrer » : une écriture réelle, décidée et enregistrée par le serveur. */
  const demarrer = async (id: string) => {
    setEnCours(id);
    setErreurAction(undefined);
    setConfirmation(undefined);

    const resultat = await modifierIntervention(id, { statut: 'en-cours' });
    setEnCours(undefined);

    if (resultat.ok && resultat.donnees) {
      setConfirmation(
        `Intervention du ${dateLisible(resultat.donnees.date)} chez ${resultat.donnees.client} démarrée.`,
      );
      await charger(periode);
    } else {
      setErreurAction(resultat.message ?? 'Démarrage refusé.');
    }
  };

  const { resume } = etat;

  if (!resume) {
    if (etat.chargement) {
      return <p className="text-sm text-slate-500">Chargement du tableau de bord…</p>;
    }
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6 max-w-2xl" role="alert">
        <p className="font-medium text-red-700 mb-1">Tableau de bord indisponible</p>
        <p className="text-sm text-slate-600 mb-4">{etat.erreur ?? 'Erreur inattendue.'}</p>
        <button
          type="button"
          onClick={() => void charger(periode)}
          className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const premierRetard = resume.retards.items[0];
  const gradient = (() => {
    // Le camembert suit les POURCENTAGES du serveur : leur somme vaut 100, donc
    // les arrêts sont justes. Aucun calcul de part n'est refait ici.
    let cumul = 0;
    const arrets: string[] = [];
    for (const compteur of resume.parStatut) {
      if (compteur.pourcentage === 0) continue;
      const couleur: Record<StatutIntervention, string> = {
        planifiee: '#3b82f6',
        'en-cours': '#f59e0b',
        terminee: '#10b981',
        annulee: '#ef4444',
      };
      arrets.push(`${couleur[compteur.statut]} ${cumul}% ${cumul + compteur.pourcentage}%`);
      cumul += compteur.pourcentage;
    }
    return arrets.length === 0 ? '#e2e8f0 0% 100%' : arrets.join(', ');
  })();

  return (
    <div className="space-y-6" aria-busy={etat.chargement}>
      {/* Période : le filtre part au serveur, l'écran n'en garde pas la liste. */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="periode-tableau" className="sr-only">
            Période d’observation
          </label>
          <CalendarClock className="w-4 h-4 text-slate-400" aria-hidden="true" />
          <select
            id="periode-tableau"
            value={resume.periode.cle}
            onChange={(evenement) => setPeriode(evenement.target.value)}
            className="bg-white border border-slate-200 rounded-lg text-sm text-slate-700 py-1.5 px-3 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {resume.periodes.map((entree) => (
              <option key={entree.cle} value={entree.cle}>
                {entree.libelle}
              </option>
            ))}
          </select>
          {resume.periode.debut && resume.periode.fin && (
            <span className="text-xs text-slate-500 mono">
              du {resume.periode.debut} au {resume.periode.fin}
            </span>
          )}
          {/* Le jour vient du SERVEUR : le navigateur peut être sur un autre fuseau. */}
          <span className="text-xs text-slate-400">
            aujourd’hui {dateLisible(resume.aujourdHui)}
          </span>
        </div>
        <span className="text-xs text-slate-400">serveur {resume.version}</span>
      </div>

      {etat.erreur && (
        <div
          role="alert"
          className="bg-white border border-amber-200 rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
        >
          <p className="text-sm text-slate-700">
            Mise à jour impossible : {etat.erreur} Les chiffres affichés viennent de la dernière
            lecture réussie.
          </p>
          <button
            type="button"
            onClick={() => void charger(periode)}
            className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-300 rounded-lg hover:bg-slate-50 flex-shrink-0"
          >
            Réessayer
          </button>
        </div>
      )}

      {confirmation && (
        <p role="status" className="text-sm text-green-700">
          {confirmation}
        </p>
      )}
      {erreurAction && (
        <p role="alert" className="text-sm text-red-700">
          {erreurAction}
        </p>
      )}

      {/* Alerte : les retards sont calculés HORS période — un oubli reste un oubli. */}
      {resume.retards.total > 0 && premierRetard && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
            <AlertTriangle className="w-4 h-4" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-red-800">
              Action requise
              {resume.retards.total > 1 ? ` — ${resume.retards.total} interventions` : ''}
            </h3>
            <p className="text-sm text-red-700 mt-1">
              L’intervention pour <strong className="font-semibold">{premierRetard.client}</strong>{' '}
              ({premierRetard.typeIntervention}) est en retard depuis le{' '}
              {dateLisible(premierRetard.date)}. Contactez le client ou mettez le statut à jour.
            </p>
            {/* Le détail est borné ET dédoublonné par le serveur. */}
            {resume.retards.autres > 0 && (
              <p className="text-sm text-red-700 mt-1">
                {resume.retards.autres} autre{resume.retards.autres > 1 ? 's' : ''} retard
                {resume.retards.autres > 1 ? 's' : ''}
                {resume.retards.clients.length > 0 && (
                  <>
                    {' '}
                    — clients concernés : {resume.retards.clients.join(', ')}
                    {resume.retards.clientsTotal > resume.retards.clients.length &&
                      ` et ${resume.retards.clientsTotal - resume.retards.clients.length} autre${
                        resume.retards.clientsTotal - resume.retards.clients.length > 1 ? 's' : ''
                      }`}
                  </>
                )}
                .
              </p>
            )}
          </div>
          {/* Le lien vient du SERVEUR : même critère que le compte annoncé. */}
          <Link
            to={resume.retards.lien ?? '/interventions?retard=1'}
            className="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-100 transition-colors shadow-sm flex-shrink-0"
          >
            Traiter
          </Link>
        </div>
      )}

      {/* Compteurs par statut, avec leur tendance */}
      <section
        aria-label="Compteurs par statut"
        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4"
      >
        {resume.parStatut.map((compteur) => {
          const couleur = COULEURS[compteur.statut];
          const Icone = couleur.icone;
          return (
            <div
              key={compteur.statut}
              className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden"
            >
              <span
                aria-hidden="true"
                className={`absolute top-0 left-0 w-1.5 h-full ${couleur.trait}`}
              />
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                <Icone className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
                {compteur.libelle}
              </p>
              <p className="text-3xl font-bold text-slate-800">{compteur.nombre}</p>
              <Tendance compteur={compteur} />
              {!resume.comparaison && (
                <p className="text-[11px] font-medium text-slate-400 mt-1">
                  Pas de période précédente
                </p>
              )}
            </div>
          );
        })}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Prochaine intervention */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
            <span aria-hidden="true" className="absolute top-0 left-0 w-1.5 h-full bg-red-600" />
            <div className="p-6">
              <div className="flex justify-between items-start mb-5 gap-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-red-500" aria-hidden="true" />
                    Prochaine intervention
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    L’événement le plus imminent de votre planning.
                  </p>
                </div>
              </div>

              {resume.prochaine ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                        Date
                      </p>
                      <p className="text-sm font-semibold text-slate-800">
                        {dateLisible(resume.prochaine.date)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                        Client
                      </p>
                      <p className="text-sm font-semibold text-slate-800">
                        {resume.prochaine.client}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                        Type
                      </p>
                      <p className="text-sm font-semibold text-slate-800">
                        {resume.prochaine.typeIntervention}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                        Lieu
                      </p>
                      <p className="text-sm font-semibold text-slate-800">
                        {resume.prochaine.lieu}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      type="button"
                      disabled={enCours === resume.prochaine.id}
                      onClick={() => void demarrer(resume.prochaine!.id)}
                      title="Passe l’intervention en « en cours » : c’est enregistré par le serveur."
                      className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors shadow-sm flex items-center gap-2 disabled:opacity-60"
                    >
                      <Play className="w-4 h-4" aria-hidden="true" />
                      Démarrer
                    </button>
                    {/* Ce lien vient du serveur : il ouvre CETTE intervention. */}
                    <Link
                      to={resume.lienProchaine ?? '/interventions'}
                      className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" aria-hidden="true" />
                      Voir les détails
                    </Link>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-600">
                  Aucune intervention planifiée à venir. Rien à préparer.
                </p>
              )}
            </div>
          </div>

          {/* Répartition */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-base font-bold text-slate-800 mb-6">
              Répartition des interventions
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
              <div className="relative w-40 h-40 flex-shrink-0">
                <div
                  className="w-full h-full rounded-full shadow-inner"
                  style={{ background: `conic-gradient(${gradient})` }}
                  role="img"
                  aria-label={`Répartition : ${resume.parStatut
                    .map((compteur) => `${compteur.libelle} ${compteur.pourcentage}%`)
                    .join(', ')}`}
                />
                <div className="absolute inset-0 m-auto w-24 h-24 bg-white rounded-full flex flex-col items-center justify-center shadow-sm border border-slate-100">
                  <span className="text-2xl font-bold text-slate-800">{resume.total}</span>
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
                    Total
                  </span>
                </div>
              </div>
              <div className="space-y-3 w-full max-w-xs">
                {resume.parStatut.map((compteur) => (
                  <div key={compteur.statut} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className={`w-3 h-3 rounded-full ${COULEURS[compteur.statut].puce}`}
                      />
                      <span className="text-slate-600">{compteur.libelle}</span>
                    </div>
                    <span className="font-bold text-slate-800">
                      {compteur.nombre}{' '}
                      <span className="text-xs font-normal text-slate-400">
                        ({compteur.pourcentage}%)
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Actions rapides */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-base font-bold text-slate-800 mb-4">Actions rapides</h2>
            <div className="space-y-3">
              <Link
                to="/interventions"
                className="w-full flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium text-slate-700"
              >
                <span className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                  <CalendarPlus className="w-4 h-4" aria-hidden="true" />
                </span>
                Planifier une intervention
              </Link>
              <Link
                to="/clients"
                className="w-full flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 hover:border-slate-300 transition-all text-sm font-medium text-slate-700"
              >
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                  <UserPlus className="w-4 h-4" aria-hidden="true" />
                </span>
                Ajouter un client
              </Link>
              {/* Pas de bouton mort : les rapports ne sont pas livrés. */}
              <button
                type="button"
                disabled
                title="Les rapports ne sont pas encore livrés : l’entrée de menu porte la mention « bientôt »."
                className="w-full flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-400 opacity-60 cursor-not-allowed"
              >
                <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <FilePlus className="w-4 h-4" aria-hidden="true" />
                </span>
                Saisir un rapport
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Le journal du compte, avec son filtre et sa pagination : la timeline de
          six lignes disait la même chose en moins complet. */}
      <JournalDuCompte />
    </div>
  );
}
